import React from 'react';
import type { ViewProps } from 'react-native';
import { theme, VStack } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../constants/credit-cards';
import type { BillingAccount } from '../../graphql/types';
import { PaymentMethodListItem } from './PaymentMethodListItem';

export type PaymentMethodListProps = {
  readonly items: readonly BillingAccount[];
  readonly onSelectItem: (account: BillingAccount) => void;
  readonly onSelectAdd: () => void;
} & ViewProps;

export const PaymentMethodList = ({
  items,
  onSelectItem,
  onSelectAdd,
}: PaymentMethodListProps): React.ReactElement => {
  const { t } = useLocalizationContext();

  return (
    <VStack customSpacing={0} divider={true} dividerColor={theme.colors.LIGHT}>
      {items.map((account) => (
        <PaymentMethodListItem
          key={account.id}
          testID={`account.edit-payment.${
            account.nickname?.split(' ')?.join('-') ?? account.id
          }`}
          accessibilityLabel={t('account.payment.title')}
          accessibilityHint={t('account.payment.title')}
          iconName={CREDIT_CARDS_ICONS[account.cardType]}
          title={account.nickname}
          subtitle={account.description}
          notice={
            account.expirationState === 'EXPIRED'
              ? t('account.payment.expired')
              : account.expirationState === 'NEARING'
              ? t('account.payment.expiring')
              : null
          }
          tag={account.isDefault ? t('account.payment.default') : null}
          onPress={() => {
            onSelectItem(account);
          }}
        />
      ))}
      <PaymentMethodListItem
        testID="account.edit-payment.new"
        accessibilityLabel={t('account.payment.title')}
        accessibilityHint={t('account.payment.title')}
        iconName={CREDIT_CARDS_ICONS.NEW}
        title={t('account.payment.new')}
        onPress={onSelectAdd}
      />
    </VStack>
  );
};
